/**
 * DI-08: Sync Failure Alert Utility
 *
 * When an order exhausts its sync retry budget (100 attempts) and transitions
 * to isSynced=2, the operator MUST be notified. Previously this was silent —
 * the order was marked as permanently failed with no visible feedback.
 *
 * This utility:
 *  1. Shows a visible antd notification (message.error) when running in a UI context
 *  2. Logs a distinctive console.error for background sync / headless contexts
 *  3. Dispatches a custom DOM event so other components (e.g. dashboard) can react
 */

import { message } from "antd";

const SYNC_FAILURE_EVENT = "pos:syncFailure";

/**
 * Call this whenever an order transitions to isSynced=2.
 *
 * @param {Object} params
 * @param {string} params.sOrderID   - The order's unique ID
 * @param {string} [params.documentno] - Human-readable document number (if available)
 * @param {string} [params.orderType]  - "regular" | "layaway"
 * @param {number} [params.syncAttempts] - Number of attempts made before giving up
 */
export function notifySyncFailure({ sOrderID, documentno, orderType = "regular", syncAttempts = 100 }) {
  const orderLabel = documentno || sOrderID?.slice(0, 8) || "unknown";
  const typeLabel = orderType === "layaway" ? "Layaway order" : "Order";

  // 1. Always log with a distinctive prefix for grep-ability
  console.error(
    `[DI-08] SYNC PERMANENTLY FAILED: ${typeLabel} ${orderLabel} failed after ${syncAttempts} attempts. ` +
    `Order marked isSynced=2 in local DB. Manual intervention required. sOrderID=${sOrderID}`
  );

  // 2. Show visible notification to the operator (safe to call even if no UI mounted —
  //    antd's message simply no-ops if there's no message container)
  try {
    message.error({
      content: `${typeLabel} ${orderLabel} failed to sync after ${syncAttempts} retries. Please contact support.`,
      duration: 10, // Keep visible longer than default (3s) — this is critical
      key: `sync-fail-${sOrderID}`, // Deduplicate if called multiple times for same order
    });
  } catch {
    // Non-UI context (background worker, test) — notification already went to console.error above
  }

  // 3. Dispatch DOM event so dashboard or monitoring components can react
  try {
    window.dispatchEvent(
      new CustomEvent(SYNC_FAILURE_EVENT, {
        detail: { sOrderID, documentno, orderType, syncAttempts, timestamp: Date.now() },
      })
    );
  } catch {
    // SSR or non-browser context — skip
  }
}

export { SYNC_FAILURE_EVENT };
